import { useCallback, useRef, useState } from "react";
import type React from "react";
import {
  Box,
  FileButton,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconCloudUpload, IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import "./UploadDropzone.scss";

interface UploadDropzoneProps {
  /** MIME / extension hint forwarded to the native picker. */
  accept: string;
  busy?: boolean;
  multiple?: boolean;
  onFilesSelected: (files: File[]) => void;
}

/**
 * Drag-and-drop file picker with a click-to-browse fallback.
 *
 * Native HTML5 DnD so we do not pull in `@mantine/dropzone`. Filtering by
 * extension happens upstream in the page that owns this component, since
 * the accepted set comes from the platform config.
 */
const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  accept,
  busy,
  multiple = false,
  onFilesSelected,
}) => {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const dragDepthRef = useRef(0);

  const handleDragEnter = useCallback((event: React.DragEvent): void => {
    event.preventDefault();
    dragDepthRef.current += 1;
    setHover(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent): void => {
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setHover(false);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent): void => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent): void => {
      event.preventDefault();
      dragDepthRef.current = 0;
      setHover(false);
      const dropped = Array.from(event.dataTransfer.files);
      if (dropped.length === 0) return;
      onFilesSelected(multiple ? dropped : [dropped[0]]);
    },
    [multiple, onFilesSelected],
  );

  return (
    <Box
      className={`upload-dropzone${hover ? " upload-dropzone--hover" : ""}${busy ? " upload-dropzone--busy" : ""}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      role="button"
      aria-label={t("upload.dropzone.ariaLabel")}
      aria-busy={busy}
    >
      <Stack align="center" gap="xs">
        <IconCloudUpload size={28} stroke={1.5} />
        <Text size="sm" fw={500}>
          {t("upload.dropzone.headline")}
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          {t("upload.dropzone.hint")}
        </Text>
        <Group gap="xs" mt={4}>
          <FileButton
            onChange={(files) => {
              if (!files) return;
              const arr = Array.isArray(files) ? files : [files];
              if (arr.length === 0) return;
              onFilesSelected(arr);
            }}
            accept={accept}
            multiple={multiple}
            disabled={busy}
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                leftSection={<IconUpload size={14} />}
                loading={busy}
              >
                {t("upload.dropzone.browse")}
              </Button>
            )}
          </FileButton>
        </Group>
      </Stack>
    </Box>
  );
};

export default UploadDropzone;
